/**
 * Firestore listeners for Payment Links on the unit dashboard and management pages.
 */

import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { PaymentLink, PaymentLinkActivity, PaymentLinkMethod, PaymentLinkPayment } from '@masonic-tools/shared-types';
import { mapActivityDoc, mapPaymentDoc, mapPaymentLinkDoc } from '@/lib/unit-payment-links';
import { parsePlatformPaymentSettings, resolveOnlinePaymentMethods } from '@/lib/available-payment-methods';

export const PAYMENT_LINK_MANAGER_ROLES = ['Steward', 'Secretary', 'Treasurer'];

/** Whether the signed-in user can manage this unit's payment links. */
export function useCanManagePaymentLinks(unitId: string | undefined): boolean {
  const { userProfile } = useAuth();
  if (!unitId || !userProfile) return false;
  if (userProfile.isAdmin === true) return true;
  const role = userProfile.units?.find((u: { unitId: string }) => u.unitId === unitId)?.role;
  return PAYMENT_LINK_MANAGER_ROLES.includes(role || '');
}

export interface PaymentLinkSetup {
  loading: boolean;
  unitName: string;
  availableMethods: PaymentLinkMethod[];
  /** Platform processing fee per method, in pence. */
  feePence: Record<PaymentLinkMethod, number>;
  creditsPerPayment: number;
}

/** Methods the unit can use, platform fees and credits per payment. */
export function usePaymentLinkSetup(unitId: string | undefined): PaymentLinkSetup {
  const [state, setState] = useState<PaymentLinkSetup>({
    loading: true,
    unitName: '',
    availableMethods: [],
    feePence: { stripe: 0, sumup: 0, slickpay: 0 },
    creditsPerPayment: 1,
  });

  useEffect(() => {
    if (!unitId) return;
    let cancelled = false;
    (async () => {
      try {
        const [platformSnap, unitSnap] = await Promise.all([
          getDoc(doc(db, 'systemSettings', 'platform')),
          getDoc(doc(db, 'units', unitId)),
        ]);
        const platformData = platformSnap.exists() ? platformSnap.data() : undefined;
        const platform = parsePlatformPaymentSettings(platformData);
        const unit = unitSnap.exists() ? unitSnap.data() : undefined;
        const methods = resolveOnlinePaymentMethods(unit as any, platform, { requireSumUpKey: true });
        const credits = Number((platformData as any)?.paymentLinks?.creditsPerPayment);
        if (cancelled) return;
        setState({
          loading: false,
          unitName: typeof unit?.name === 'string' ? unit.name : '',
          availableMethods: methods,
          feePence: {
            stripe: Math.round(platform.stripe.transactionFee * 100),
            sumup: Math.round(platform.sumup.transactionFee * 100),
            slickpay: Math.round(platform.slickpay.transactionFee * 100),
          },
          creditsPerPayment: Number.isFinite(credits) ? Math.max(0, Math.floor(credits)) : 1,
        });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  return state;
}

export function usePaymentLinks(unitId: string | undefined): { links: PaymentLink[]; loading: boolean; error: string | null } {
  const [links, setLinks] = useState<PaymentLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!unitId) return;
    const q = query(collection(db, 'units', unitId, 'paymentLinks'), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        setLinks(snap.docs.map((d) => mapPaymentLinkDoc(d.id, unitId, d.data())));
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [unitId]);

  return { links, loading, error };
}

export function usePaymentLink(unitId: string | undefined, linkId: string | undefined) {
  const [link, setLink] = useState<PaymentLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!unitId || !linkId) return;
    return onSnapshot(
      doc(db, 'units', unitId, 'paymentLinks', linkId),
      (snap) => {
        if (!snap.exists()) {
          setNotFound(true);
          setLink(null);
        } else {
          setNotFound(false);
          setLink(mapPaymentLinkDoc(snap.id, unitId, snap.data()));
        }
        setLoading(false);
      },
      () => {
        setNotFound(true);
        setLoading(false);
      },
    );
  }, [unitId, linkId]);

  return { link, loading, notFound };
}

export function usePaymentLinkPayments(unitId: string | undefined, linkId: string | undefined) {
  const [payments, setPayments] = useState<PaymentLinkPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!unitId || !linkId) return;
    const q = query(collection(db, 'units', unitId, 'paymentLinks', linkId, 'payments'), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        setPayments(snap.docs.map((d) => mapPaymentDoc(d.id, unitId, linkId, d.data())));
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [unitId, linkId]);

  return { payments, loading, error };
}

export function usePaymentLinkActivity(unitId: string | undefined, linkId: string | undefined) {
  const [activity, setActivity] = useState<PaymentLinkActivity[]>([]);
  useEffect(() => {
    if (!unitId || !linkId) return;
    const q = query(collection(db, 'units', unitId, 'paymentLinks', linkId, 'activity'), orderBy('createdAt', 'desc'), limit(100));
    return onSnapshot(
      q,
      (snap) => setActivity(snap.docs.map((d) => mapActivityDoc(d.id, d.data()))),
      () => setActivity([]),
    );
  }, [unitId, linkId]);
  return activity;
}

/** Re-render every minute so relative times and expiry badges stay current. */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** IDs that appeared since the first render (used to highlight new payments as they arrive). */
export function useNewIds(ids: string[], ready: boolean, highlightMs = 6000): Set<string> {
  const [seen, setSeen] = useState<Set<string> | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const key = useMemo(() => ids.join(','), [ids]);

  useEffect(() => {
    if (!ready) return;
    if (seen === null) {
      setSeen(new Set(ids));
      return;
    }
    const added = ids.filter((id) => !seen.has(id));
    if (added.length === 0) return;
    setSeen(new Set(ids));
    setFresh((f) => new Set([...f, ...added]));
    const t = setTimeout(() => {
      setFresh((f) => {
        const next = new Set(f);
        added.forEach((id) => next.delete(id));
        return next;
      });
    }, highlightMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);

  return fresh;
}
